import React from 'react';
import { motion } from 'framer-motion';
import { useBranch } from '~/contexts/BranchContext';
import { useTranslation } from 'react-i18next';
import { FaStore, FaCircle, FaUtensils, FaWifi, FaParking, FaCreditCard, FaUsers } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import * as FaIcons from 'react-icons/fa';
import * as FiIcons from 'react-icons/fi';
import type { Features } from '../../../types/new_types';

const iconLibraries: Record<string, Record<string, IconType>> = {
	Fa: FaIcons as unknown as Record<string, IconType>,
	Fi: FiIcons as unknown as Record<string, IconType>,
};

const resolveIconComponent = (iconName: string): IconType | undefined => {
	const trimmedName = iconName.trim();
	if (!trimmedName) return undefined;
	const prefix = trimmedName.slice(0, 2);
	const library = iconLibraries[prefix];
	if (library && library[trimmedName]) {
		return library[trimmedName];
	}
	for (const lib of Object.values(iconLibraries)) {
		if (lib[trimmedName]) {
			return lib[trimmedName];
		}
	}
	return undefined;
};

interface FeatureCardProps {
	feature: Features;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ feature }) => {
	const { theme } = useBranch();
	const { i18n } = useTranslation();
	const isRTL = i18n.language === 'ar';

	// Helper function to get localized text
	const getLocalizedText = (englishText: string, arabicObj?: { name?: string; description?: string } | null, field: 'name' | 'description' = 'name') => {
		if (isRTL && arabicObj && arabicObj[field]) {
			return arabicObj[field];
		}
		return englishText;
	};

	// Smart default icon selector based on feature name
	const getSmartDefaultIcon = (featureName: string, size: number = 24) => {
		const lowerName = (featureName ?? '').toLowerCase();

		if (lowerName.includes('wi-fi') || lowerName.includes('wifi') || lowerName.includes('internet') || lowerName.includes('وايفاي')) {
			return <FaWifi size={size} className="opacity-70" />;
		} else if (lowerName.includes('parking') || lowerName.includes('موقف')) {
			return <FaParking size={size} className="opacity-70" />;
		} else if (lowerName.includes('payment') || lowerName.includes('card') || lowerName.includes('دفع')) {
			return <FaCreditCard size={size} className="opacity-70" />;
		} else if (lowerName.includes('menu') || lowerName.includes('food') || lowerName.includes('قائمة')) {
			return <FaUtensils size={size} className="opacity-70" />;
		} else if (lowerName.includes('customer') || lowerName.includes('guest') || lowerName.includes('عميل')) {
			return <FaUsers size={size} className="opacity-70" />;
		} else {
			return <FaStore size={size} className="opacity-70" />;
		}
	};

	// Default icon placeholder component
	const DefaultFeatureIcon = ({ size = 24 }: { size?: number }) => (
		<div className="flex items-center justify-center">
			{getSmartDefaultIcon(localizedName, size)}
		</div>
	);

	// Default child icon placeholder component
	const DefaultChildIcon = ({ size = 12, childName = '' }: { size?: number; childName?: string }) => (
		<div className="flex items-center justify-center">
			{childName ? getSmartDefaultIcon(childName, size) : <FaCircle size={size} className="opacity-50" />}
		</div>
	);

	const { icon, name, description, children, ar } = feature;

	// Get localized content
	const localizedName = getLocalizedText(name, ar, 'name');
	const localizedDescription = getLocalizedText(description, ar, 'description');

	const renderNamedIcon = (
		rawIcon: string,
		size: number,
		fallbackNode: React.ReactNode,
		textClassName: string
	) => {
		const trimmed = rawIcon.trim();
		if (!trimmed) return fallbackNode;
		const IconFromLibrary = resolveIconComponent(trimmed);
		if (IconFromLibrary) {
			return <IconFromLibrary size={size} className="opacity-70" />;
		}
		const characterCount = Array.from(trimmed).length;
		if (characterCount <= 2) {
			return <span className={textClassName}>{trimmed}</span>;
		}
		return fallbackNode;
	};

	const renderFeatureIcon = () => {
		const fallback = <DefaultFeatureIcon size={24} />;
		const trimmed = icon?.trim() ?? '';
		if (!trimmed) return fallback;
		if (trimmed.startsWith('http')) {
			return (
				<>
					<img
						src={trimmed}
						alt="feature icon"
						className="w-8 h-8 object-contain rounded"
						onError={(e) => {
							e.currentTarget.style.display = 'none';
							e.currentTarget.nextElementSibling?.classList.remove('hidden');
						}}
					/>
					<div className="hidden">{fallback}</div>
				</>
			);
		}
		return renderNamedIcon(trimmed, 24, fallback, 'text-2xl');
	};

	const renderChildIcon = (
		iconValue: string | undefined | null,
		fallbackName: string
	) => {
		const trimmed = iconValue?.trim() ?? '';
		const fallback = <DefaultChildIcon size={12} childName={fallbackName} />;
		if (!trimmed) return fallback;
		if (trimmed.startsWith('http')) {
			return (
				<>
					<img
						src={trimmed}
						alt="child icon"
						className="w-5 h-5 object-contain rounded"
						onError={(e) => {
							e.currentTarget.style.display = 'none';
							e.currentTarget.nextElementSibling?.classList.remove('hidden');
						}}
					/>
					<div className="hidden">{fallback}</div>
				</>
			);
		}
		return renderNamedIcon(trimmed, 16, fallback, 'text-sm');
	};

	return (
		<motion.div
			className={`relative bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-lg border border-gray-100 p-6 hover:shadow-2xl transition-all duration-500 group overflow-hidden ${isRTL ? 'text-right' : 'text-left'}`}
			initial={{ opacity: 0, y: 30 }}
			whileInView={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
			viewport={{ once: true }}
			whileHover={{ y: -8, scale: 1.02 }}
		>
			{/* Gradient overlay on hover */}
			<div
				className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-3xl"
				style={{
					background: `linear-gradient(135deg, ${theme?.primaryColor || '#3B82F6'}15, ${theme?.primaryColor || '#3B82F6'}05)`
				}}
			/>

			{/* Content */}
			<div className="relative z-10">
				{/* Header with Icon and Title */}
				<div className={`flex items-center mb-4 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
					<div
						className={`w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 shadow-lg ${isRTL ? 'ml-4' : 'mr-4'}`}
						style={{
							background: `linear-gradient(135deg, ${theme?.primaryColor || '#3B82F6'}20, ${theme?.primaryColor || '#3B82F6'}10)`,
							color: theme?.primaryColor || '#3B82F6'
						}}
					>
						{renderFeatureIcon()}
					</div>
					<div className="flex-1">
						<h3 className="text-xl font-bold text-gray-800 mb-1 group-hover:text-gray-900 transition-colors duration-300">{localizedName}</h3>
						<p className="text-gray-600 text-sm leading-relaxed">{localizedDescription}</p>
					</div>
				</div>

				{/* Children Features */}
				{children && children.length > 0 && (
					<div className="mt-4 pt-4 border-t border-gray-100">
						<div className="space-y-3">
							{children.map((child, index) => {
								const childLocalizedName = getLocalizedText(child.name, child.ar, 'name');
								const childLocalizedDescription = getLocalizedText(child.description, child.ar, 'description');

								return (
									<motion.div
										key={child.id}
										className={`flex items-start group/child ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
										initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
										whileInView={{ opacity: 1, x: 0 }}
										transition={{ duration: 0.3, delay: index * 0.1 }}
										viewport={{ once: true }}
									>
										<div
											className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 group-hover/child:scale-110 transition-transform duration-200 ${isRTL ? 'ml-3' : 'mr-3'}`}
											style={{
												background: `linear-gradient(135deg, ${theme?.primaryColor || '#3B82F6'}15, ${theme?.primaryColor || '#3B82F6'}08)`,
												color: theme?.primaryColor || '#3B82F6'
											}}
										>
											{renderChildIcon(child.icon, childLocalizedName)}
										</div>
										<div className="flex-1 min-w-0">
											<p className="font-semibold text-gray-800 text-sm mb-1">{childLocalizedName}</p>
											<p className="text-gray-500 text-xs leading-relaxed">{childLocalizedDescription}</p>
										</div>
									</motion.div>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</motion.div>
	);
};

export default FeatureCard;